// src/pages/ops.ts —— 06 C-19 Webhook / 任务日志、C-23 操作记录、C-26 告警页定义（PAGES_OPS 经 pages/index 注册，后序覆盖 PAGES_SHELLS 同名搭壳页）。
// C-02 仪表盘为 dash 原生页（domains/dash.dashboard.ts），其文案键在本包（ops 包经 pagePacks 进入字典）。
// 写动作（告警确认 / Webhook 重放 / 查 Task）走「宿主 × 动作键」在途锁；logs / alerts 徽标取 GET /me badges（C-24），写成功后显式 refreshMe 再发 profile。
// openOperationDrawer 签名受契约锁约束（TD-851），供用户 360 操作记录行复用
import { ApiError } from '../../../shared/src/api/client';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { toMicro } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openDrawer, reloadCurrent, sessionGuard } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_OPS, REWRITES_OPS } from '../i18n/ops';
import type { AlertRow, ApiAggRow, ApiCallRow, OpDetail, OpRow, TaskResult, TaskRow, WebhookRow } from './ops.types';
import { openUser360 } from './shells';
import { defineView, type ActionDef, type DrawerAction, type DrawerSpec, type ErasedView, type Need, type PageMap } from './types';
import { cell, chip, chipOf, type CellVals, type ChipKind, type Tone } from './ui';

const LOGS_EDIT: Need = { module: 'logs', bit: 'edit' };
const EMPTY = '—';
const DIGEST_MAX = 40; // 06 C-23：resp_digest 截断 40 字
const OK_RATE_RED = 950_000n; // 06 C-19：成功率 < 95% 红章（微单位比值）
const CODE_PRECONDITION = 1012; // 02 §13：状态已变（告警已确认 / 回调非可重放态）

const tz = (ctx: AdminCtx): string | undefined => ctx.s.sh.timezone || undefined;
const when = (ctx: AdminCtx, iso: string | null): string => fmtDateTime(iso, { tz: tz(ctx) });
const textOr = (t: Dict, key: string, fallback: string): string => str(t, key) || fallback;
const dash = (v: string | number | null | undefined): string => (v === null || v === undefined || v === '' ? EMPTY : String(v));
const refOf = (type: string | null, id: number | null): string => (type && id !== null ? `${type}:${id}` : EMPTY);
const json = (v: unknown): string => JSON.stringify(v ?? null, null, 2);

// ---- 状态章映射（06 各节章映射；未映射值 gray 章 + 原值并告警，X-34） ----

type ChipDef = readonly [ChipKind, (t: Dict) => string];
type ChipDefs = Readonly<Record<string, ChipDef>>;

const label = (key: string) => (t: Dict): string => textOr(t, key, key);

// C-19 Tab0 处理结果（03 webhook_events.status；X-17）
const HOOK_STATUS: ChipDefs = {
  received: ['amber', label('stReceived')],
  processed: ['green', (t) => `200 · ${str(t, 'stDone')}`],
  failed: ['red', (t) => `500 · ${str(t, 'stFail')}`],
  skipped: ['gray', label('stSkipped')],
};
// C-19 Tab1 任务状态（upstream_tasks.status）
const TASK_STATUS: ChipDefs = { success: ['green', label('stFinished')], pending: ['amber', label('stRunning')], fail: ['red', label('stFail')] };
// C-26 级别 / 状态
const SEVERITY: ChipDefs = { warn: ['amber', label('sevWarn')], critical: ['red', label('sevCritical')] };
const ALERT_STATUS: ChipDefs = { open: ['red', label('stAlertOpen')], acked: ['gray', label('stAlertAcked')] };
// C-23 结果
const OP_RESULT: ChipDefs = { ok: ['green', label('stDone')], fail: ['red', label('stFail')] };
// C-23 主体类型 / 渠道文案
const ACTOR_KIND: Readonly<Record<string, string>> = { user: 'actorUser', agent: 'actorAgent', admin: 'actorAdmin', system: 'actorSystem', bot: 'actorBot', webhook: 'actorWebhook' };
const CHANNEL: Readonly<Record<string, string>> = { h5: 'chH5', miniapp: 'chMiniapp', bot: 'chBot', agent: 'chAgent', admin: 'chAdmin', worker: 'chWorker', webhook: 'chWebhook', system: 'chSystem' };

function chipBy(t: Dict, defs: ChipDefs, v: string, column: string): CellVals {
  const d = defs[v];
  return d ? chip(d[0], d[1](t)) : chipOf({}, v, column);
}

// 章文案 / facet 选项文案：未知值显示原值（P-12）
const textBy = (defs: ChipDefs) => (t: Dict, v: string): string => defs[v]?.[1](t) ?? v;
const mapText = (m: Readonly<Record<string, string>>) => (t: Dict, v: string): string => {
  const k = m[v];
  return k ? textOr(t, k, v) : v;
};
const hookText = textBy(HOOK_STATUS);
const taskText = textBy(TASK_STATUS);
const sevText = textBy(SEVERITY);
const alertText = textBy(ALERT_STATUS);
const resultText = textBy(OP_RESULT);
const actorText = mapText(ACTOR_KIND);
const channelText = mapText(CHANNEL);

const HOOK_TONE: Readonly<Record<string, Tone>> = { processed: 'good', failed: 'bad', received: 'warn' };

// 成功率 0~1 比值串 → 百分比（D8-12 截断 1 位、去尾随 .0）；非法值原样
function okPct(rate: string): { text: string; low: boolean } {
  try {
    const m = toMicro(rate);
    const tenths = (m * 1000n) / 1_000_000n;
    const int = tenths / 10n;
    const frac = tenths % 10n;
    return { text: `${int}${frac === 0n ? '' : `.${frac}`}%`, low: m < OK_RATE_RED };
  } catch {
    return { text: rate, low: false };
  }
}

// ---- 在途锁（宿主 × 动作键）：行内与抽屉同一动作共用一把锁，结束即释放；错误统一 toast，会话已换则静默 ----

const inflight = new WeakMap<object, Set<string>>();

export function guarded(key: string, fn: (ctx: AdminCtx) => Promise<void>): (ctx: AdminCtx) => Promise<void> {
  return async (ctx) => {
    let keys = inflight.get(ctx.host);
    if (!keys) {
      keys = new Set();
      inflight.set(ctx.host, keys);
    }
    if (keys.has(key)) return;
    keys.add(key);
    const alive = sessionGuard(ctx);
    try {
      await fn(ctx);
    } catch (e) {
      if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    } finally {
      keys.delete(key);
    }
  };
}

// C-24：logs / alerts 徽标取 GET /me 的 badges。写成功后：重载当前表 → 重取 /me（外壳按 badgesFromMe 重算）→ profile（外壳重算 review / agentWd）
async function afterWrite(ctx: AdminCtx, alive: () => boolean): Promise<void> {
  await reloadCurrent(ctx);
  if (!alive()) return;
  await ctx.gp.session.refreshMe();
  if (alive()) ctx.events.emit('profile', {});
}

// 1012：状态已被他处改变（已确认 / 非可重放态）→ toast 后关闭抽屉、刷新列表，并重取 /me（徽标数据源已随之变化，C-24）
async function onWriteError(ctx: AdminCtx, alive: () => boolean, e: unknown): Promise<void> {
  if (!alive()) return;
  ctx.gp.ui.toast(ctx.gp.ui.err(e));
  if (e instanceof ApiError && e.code === CODE_PRECONDITION) {
    closeDrawer(ctx);
    await reloadCurrent(ctx);
    if (alive()) await ctx.gp.session.refreshMe();
  }
}

// ---- C-26 告警 ----

export function ackAlert(ctx: AdminCtx, r: AlertRow): Promise<void> {
  return guarded(`ack:${r.id}`, async (c) => {
    const alive = sessionGuard(c);
    try {
      await c.gp.api.call('POST /api/admin/alerts/{id}/ack', { path: { id: r.id }, body: {} });
    } catch (e) {
      await onWriteError(c, alive, e);
      return;
    }
    if (!alive()) return;
    c.gp.ui.toast(str(c.gp.ui.dict(), 'toastAck'));
    closeDrawer(c);
    await afterWrite(c, alive);
  })(ctx);
}

function alertDrawer(ctx: AdminCtx, r: AlertRow): void {
  const t = ctx.t;
  const open = r.status === 'open';
  const actions: DrawerAction[] = open
    ? [{ label: 'aAck', kind: 'green', need: LOGS_EDIT, run: (c) => ackAlert(c, r) }]
    : [{ label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) }];
  openDrawer(ctx, {
    kind: str(t, 'alerts'),
    title: r.title,
    subtitle: `${r.kind} · ${sevText(t, r.severity)}`,
    fields: [
      { k: str(t, 'obsAlertType'), v: r.kind },
      { k: str(t, 'shlAlertLevel'), v: sevText(t, r.severity) },
      { k: str(t, 'shlAlertCount'), v: String(r.count) },
      { k: str(t, 'shlAlertFirst'), v: when(ctx, r.first_at) },
      { k: str(t, 'shlAlertLast'), v: when(ctx, r.last_at) },
      { k: str(t, 'cStatus'), v: alertText(t, r.status) },
      { k: str(t, 'obsAckBy'), v: r.acked_by_account ? `${r.acked_by_account} · ${when(ctx, r.acked_at)}` : EMPTY },
      { k: 'dedupe_key', v: r.dedupe_key, wide: true },
    ],
    json: json(r.detail),
    actions,
  });
}

function alertsView(key: 'open' | 'acked' | 'all'): ErasedView {
  return defineView<AlertRow>({
    key,
    source: { route: 'GET /api/admin/alerts', tabParam: 'status' },
    echo: '/admin/alerts',
    // 06 C-26 列序；status 由页签下发，工具条不重复渲染 status 下拉（只声明 severity facet）
    cols: [
      { label: 'shlAlertKind' }, { label: 'shlAlertLevel', facet: 'severity', facetLabel: sevText }, { label: 'shlAlertCount' },
      { label: 'shlAlertFirst', sortKey: 'first_at' }, { label: 'shlAlertLast', sortKey: 'last_at' }, { label: 'cStatus' }, { label: 'shlAlertAcker' },
    ],
    cells: (c, r) => [
      cell(r.kind, r.title, true),
      chipBy(c.t, SEVERITY, r.severity, 'alerts.severity'),
      cell(r.count),
      cell(when(c, r.first_at)),
      cell(when(c, r.last_at)),
      chipBy(c.t, ALERT_STATUS, r.status, 'alerts.status'),
      r.acked_by_account ? cell(r.acked_by_account, when(c, r.acked_at)) : cell(EMPTY),
    ],
    open: alertDrawer,
    rowActions: (): readonly ActionDef<AlertRow>[] => [
      { label: 'aAck', need: LOGS_EDIT, show: (r) => r.status === 'open', run: (c, r) => ackAlert(c, r) },
      { label: 'aDetail', run: (c, r) => alertDrawer(c, r) },
    ],
    csvName: 'alerts',
  });
}

// ---- C-19 Tab0 Webhook 事件 ----

// 06 C-19：status ∈ {failed, received} 可重放；source=test 为自签测试回调，后端恒 1012（TD-727），不给入口
export const canReplay = (r: Pick<WebhookRow, 'status' | 'source'>): boolean => (r.status === 'failed' || r.status === 'received') && r.source !== 'test';

export function replayWebhook(ctx: AdminCtx, r: WebhookRow): Promise<void> {
  return guarded(`replay:${r.id}`, async (c) => {
    const alive = sessionGuard(c);
    try {
      await c.gp.api.call('POST /api/admin/webhooks/{id}/replay', { path: { id: r.id }, body: {} });
    } catch (e) {
      await onWriteError(c, alive, e);
      return;
    }
    if (!alive()) return;
    c.gp.ui.toast(str(c.gp.ui.dict(), 'toastRetry'));
    closeDrawer(c);
    await afterWrite(c, alive);
  })(ctx);
}

// 抽屉：列表行无 payload 字段（无单条详情出口）→ 不渲染「原始 Payload」JSON 区（偏差 PB-2）
function webhookDrawer(ctx: AdminCtx, r: WebhookRow): void {
  const t = ctx.t;
  const actions: DrawerAction[] = [
    ...(canReplay(r) ? [{ label: 'aReplay', kind: '' as const, need: LOGS_EDIT, run: (c: AdminCtx) => replayWebhook(c, r) }] : []),
    { label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) },
  ];
  openDrawer(ctx, {
    kind: 'Webhook',
    title: r.event_type,
    subtitle: `task ${dash(r.task_id)}`,
    fields: [
      { k: 'task_id', v: dash(r.task_id) },
      { k: str(t, 'cResult'), v: hookText(t, r.status) },
      { k: str(t, 'sigVerify'), v: r.signature_ok ? 'RSA-SHA256 ✓' : 'RSA-SHA256 ✗' },
      { k: str(t, 'cRetries'), v: String(r.retries) },
      { k: 'trace_id', v: dash(r.trace_id), wide: true },
      { k: str(t, 'obsRefDoc'), v: refOf(r.ref_type, r.ref_id) },
      ...(r.status === 'failed' && r.error ? [{ k: str(t, 'obsError'), v: r.error, wide: true }] : []),
    ],
    actions,
  });
}

const webhookView = defineView<WebhookRow>({
  key: 'webhook',
  source: { route: 'GET /api/admin/webhooks', tabParam: 'none' },
  echo: '/admin/webhooks',
  cols: [
    { label: 'cTaskId' }, { label: 'cEventType', facet: 'event_type' }, { label: 'cCardOrder' }, { label: 'cResult', facet: 'result', facetLabel: hookText },
    { label: 'cRetries' }, { label: 'cTime', sortKey: 'received_at' },
  ],
  cells: (c, r) => [
    cell(dash(r.task_id), '', true),
    cell(r.event_type, '', true),
    cell(dash(r.ref_label), '', true),
    chipBy(c.t, HOOK_STATUS, r.status, 'webhooks.status'),
    cell(r.retries),
    cell(when(c, r.received_at)),
  ],
  open: webhookDrawer,
  rowActions: (): readonly ActionDef<WebhookRow>[] => [
    { label: 'aDetail', run: (c, r) => webhookDrawer(c, r) },
    { label: 'aRetry', need: LOGS_EDIT, show: canReplay, run: (c, r) => replayWebhook(c, r) },
  ],
  csvName: 'logs',
});

// ---- C-19 Tab1 任务 Task ----

// 上游只读探测（07 §9.2-8，不写 operation_logs）；1401 / 1403 经 ui.err 映射（TD-846）
export function queryTask(ctx: AdminCtx, r: Pick<TaskRow, 'task_id'>): Promise<void> {
  return guarded(`task:${r.task_id}`, async (c) => {
    const alive = sessionGuard(c);
    const res = await c.gp.api.call<TaskResult>('POST /api/admin/tasks/{id}/query', { path: { id: r.task_id }, body: {} });
    if (!alive()) return;
    c.gp.ui.toast(interpolate(str(c.gp.ui.dict(), 'toastTask'), { status: res.status }));
  })(ctx);
}

function taskDrawer(ctx: AdminCtx, r: TaskRow): void {
  const t = ctx.t;
  openDrawer(ctx, {
    kind: 'Task',
    title: `task ${r.task_id}`,
    subtitle: dash(r.type),
    fields: [
      { k: 'task_id', v: String(r.task_id) },
      { k: str(t, 'cTaskType'), v: dash(r.type) },
      { k: str(t, 'cTaskState'), v: taskText(t, r.status) },
      { k: str(t, 'cResult'), v: dash(r.result_summary) },
      { k: str(t, 'obsRefDoc'), v: refOf(r.ref_type, r.ref_id), wide: true },
    ],
    json: json({ status: r.status, data: { task_id: r.task_id, type: r.type, result: r.result_summary } }),
    actions: [{ label: 'aQueryTask', kind: 'green', need: LOGS_EDIT, run: (c) => queryTask(c, r) }],
  });
}

// 06 C-19：pending 超 24h 状态章下「超 24h · 已告警」（只告警不自动终态，D18）
function taskStateCell(t: Dict, r: TaskRow): CellVals {
  const c = chipBy(t, TASK_STATUS, r.status, 'tasks.status');
  return r.overdue ? { ...c, sub: str(t, 'taskOverdue') } : c;
}

const taskView = defineView<TaskRow>({
  key: 'tasks',
  source: { route: 'GET /api/admin/tasks', tabParam: 'none' },
  echo: '/admin/tasks',
  cols: [{ label: 'cTaskId' }, { label: 'cTaskType' }, { label: 'cTaskState', facet: 'task_state', facetLabel: taskText }, { label: 'cResult', facet: 'result' }, { label: 'cTime', sortKey: 'created_at' }],
  cells: (c, r) => [cell(r.task_id, '', true), cell(dash(r.type), '', true), taskStateCell(c.t, r), cell(dash(r.result_summary)), cell(when(c, r.created_at))],
  open: taskDrawer,
  rowActions: (): readonly ActionDef<TaskRow>[] => [{ label: 'aQueryTask', need: LOGS_EDIT, run: (c, r) => queryTask(c, r) }],
  csvName: 'logs',
});

// ---- C-19 Tab2 API 调用 ----

const API_SIG_SAMPLE = { headers: { Authorization: 'Bearer ****', 'X-ACCESS-CODE': '****', 'X-TIMESTAMP': '…', 'X-SIGNATURE': '…' }, response: { status: 'success' } };

export function apiCallsQuery(r: Pick<ApiAggRow, 'method' | 'endpoint'>): Record<string, string | number> {
  // S-70：带 endpoint 过滤返回该接口逐次调用明细（按 created_at 倒序）
  return { endpoint: `${r.method} ${r.endpoint}`, page: 1, size: 20 };
}

function openApiDrawer(ctx: AdminCtx, r: ApiAggRow): Promise<void> {
  return guarded(`api:${r.method} ${r.endpoint}`, async (c) => {
    const alive = sessionGuard(c);
    const page = c.host.state.page;
    const res = await c.gp.api.call<{ list: readonly ApiCallRow[] }>('GET /api/admin/api-calls', { query: apiCallsQuery(r) });
    if (!alive() || c.host.state.page !== page) return;
    const t = c.gp.ui.dict();
    const rate = okPct(r.ok_rate);
    const rows = (Array.isArray(res.list) ? res.list : []).map((x) => ({
      left: `${x.status_code} · ${x.duration_ms}ms`,
      sub: `trace ${dash(x.trace_id)} · ${refOf(x.ref_type, x.ref_id)}`,
      right: when(c, x.created_at),
      rightSub: str(t, x.ok ? 'stDone' : 'stFail'),
      tone: (x.ok ? 'good' : 'bad') as Tone,
    }));
    openDrawer(c, {
      kind: 'API',
      title: `${r.method} ${r.endpoint}`,
      subtitle: r.direction,
      fields: [
        { k: str(t, 'cCalls'), v: String(r.calls_24h) },
        { k: str(t, 'cP95'), v: `${r.p95_ms}ms` },
        { k: str(t, 'cSuccess'), v: rate.text },
        { k: str(t, 'sigVerify'), v: 'X-SIGNATURE RSA-SHA256' },
      ],
      list: { title: str(t, 'recentCalls'), rows },
      json: json(API_SIG_SAMPLE),
      actions: [{ label: 'aClose', kind: 'primary', run: (cc) => closeDrawer(cc) }],
    });
  })(ctx);
}

// trace 列：前 8 位 + …（06 C-19；P8 不跳转）
const traceShort = (v: string | null): string => (v ? (v.length > 8 ? `${v.slice(0, 8)}…` : v) : EMPTY);

const apiView = defineView<ApiAggRow>({
  key: 'api',
  source: { route: 'GET /api/admin/api-calls', tabParam: 'none' },
  echo: '/admin/api-calls',
  cols: [{ label: 'cEndpoint' }, { label: 'cCalls' }, { label: 'cP95' }, { label: 'cSuccess' }, { label: 'cLastCall', sortKey: 'created_at' }, { label: 'cLastTrace' }, { label: 'cLastRef' }],
  cells: (c, r) => {
    const rate = okPct(r.ok_rate);
    return [
      cell(`${r.method} ${r.endpoint}`, r.direction, true),
      cell(r.calls_24h),
      cell(`${r.p95_ms}ms`),
      chip(rate.low ? 'red' : 'green', rate.text),
      cell(when(c, r.last_at)),
      cell(traceShort(r.last_trace_id), '', true),
      cell(dash(r.last_ref), '', true),
    ];
  },
  open: (c, r) => void openApiDrawer(c, r),
  rowActions: (): readonly ActionDef<ApiAggRow>[] => [{ label: 'aDetail', run: (c, r) => openApiDrawer(c, r) }],
  csvName: 'logs',
});

// ---- C-23 操作记录 ----

const actorSub = (t: Dict, r: Pick<OpRow, 'actor_kind' | 'actor_id'>): string => `${actorText(t, r.actor_kind)} · ${dash(r.actor_id)}`;
const targetOf = (r: Pick<OpRow, 'target_biz_no' | 'target_type' | 'target_id'>): string => r.target_biz_no || (r.target_type ? `${r.target_type}#${dash(r.target_id)}` : EMPTY);
const subjectOf = (r: Pick<OpRow, 'subject_uid' | 'subject_user_id'>): string => r.subject_uid ?? (r.subject_user_id !== null ? `#${r.subject_user_id}` : EMPTY);
const resultLine = (t: Dict, r: Pick<OpRow, 'result' | 'error_code'>): string => `${resultText(t, r.result)}${r.error_code !== null ? ` · ${r.error_code}` : ''}`;
const cut = (s: string | null, n: number): string => (s ? (s.length > n ? `${s.slice(0, n)}…` : s) : '');

// 偏差 §6-P2：关联上游调用与关联回调事件合并为一个列表，分组标题行（opApiCalls / opWebhooks）区分；组内无行显示「暂无数据」
function relatedRows(ctx: AdminCtx, t: Dict, d: OpDetail): NonNullable<DrawerSpec['list']>['rows'] {
  const calls = d.api_calls ?? [];
  const hooks = d.webhooks ?? [];
  const none = { left: str(t, 'empty') };
  return [
    { left: str(t, 'opApiCalls'), right: String(calls.length) },
    ...(calls.length === 0
      ? [none]
      : calls.map((x) => ({
          left: `${x.method} ${x.endpoint}`,
          sub: `${x.direction} · ${when(ctx, x.created_at)}`,
          right: `${x.status_code} · ${x.duration_ms}ms`,
          rightSub: cut(x.resp_digest, DIGEST_MAX),
          tone: (x.ok ? 'good' : 'bad') as Tone,
        }))),
    { left: str(t, 'opWebhooks'), right: String(hooks.length) },
    ...(hooks.length === 0 ? [none] : hooks.map((x) => ({ left: x.event_type, sub: when(ctx, x.received_at), right: hookText(t, x.status), tone: HOOK_TONE[x.status] ?? '' }))),
  ];
}

// 06 C-23 抽屉（GET /operations/{id}）：字段格 10 项、JSON「变更快照」、合并关联列表；底部 用户 360（有所涉用户时）/ 关闭
export function openOperationDrawer(ctx: AdminCtx, id: number): void {
  void guarded(`op:${id}`, async (c) => {
    const alive = sessionGuard(c);
    const page = c.host.state.page;
    const d = await c.gp.api.call<OpDetail>('GET /api/admin/operations/{id}', { path: { id } });
    if (!alive() || c.host.state.page !== page) return;
    const t = c.gp.ui.dict();
    const subject = d.subject_user_id;
    openDrawer(c, {
      kind: str(t, 'audit'),
      title: d.action,
      subtitle: `${d.actor_label} · ${when(c, d.created_at)}`,
      fields: [
        { k: str(t, 'cTime'), v: when(c, d.created_at) },
        { k: str(t, 'obsActor'), v: `${actorText(t, d.actor_kind)} · ${d.actor_label} · ${dash(d.actor_id)}` },
        { k: str(t, 'cChannel'), v: channelText(t, d.channel) },
        { k: str(t, 'obsResult'), v: resultLine(t, d) },
        { k: str(t, 'cTarget'), v: `${dash(d.target_type)} · ${dash(d.target_id)} · ${dash(d.target_biz_no)}`, wide: true },
        { k: str(t, 'obsSubject'), v: subjectOf(d) },
        { k: str(t, 'obsRefDoc'), v: refOf(d.ref_type, d.ref_id) },
        { k: 'trace_id', v: dash(d.trace_id), wide: true },
        { k: str(t, 'cIp'), v: dash(d.ip) },
        { k: 'UA', v: dash(d.user_agent), wide: true },
      ],
      json: json({ before: d.before_json, after: d.after_json }),
      jsonTitle: str(t, 'opSnapshot'),
      list: { title: `${str(t, 'opApiCalls')} · ${str(t, 'opWebhooks')}`, rows: relatedRows(c, t, d) },
      actions: [
        ...(subject !== null ? [{ label: 'aOpen360', kind: '' as const, run: (cc: AdminCtx) => openUser360(cc, subject) }] : []),
        { label: 'aClose', kind: 'primary', run: (cc) => closeDrawer(cc) },
      ],
    });
  })(ctx);
}

const auditView = defineView<OpRow>({
  key: 'all',
  source: { route: 'GET /api/admin/operations', tabParam: 'none' },
  echo: '/admin/operations',
  // 06 C-23 列表规格 7 列（原型 5 列改写，IP 移入抽屉）
  cols: [
    { label: 'cTime', sortKey: 'created_at' }, { label: 'obsActor', facet: 'actor_kind', facetLabel: actorText }, { label: 'cChannel' },
    { label: 'obsAction', facet: 'action' }, { label: 'cTarget' }, { label: 'obsSubject' }, { label: 'obsResult', facet: 'result', facetLabel: resultText },
  ],
  cells: (c, r) => {
    const res = chipBy(c.t, OP_RESULT, r.result, 'operations.result');
    return [
      cell(when(c, r.created_at)),
      cell(r.actor_label, actorSub(c.t, r)),
      cell(channelText(c.t, r.channel)),
      cell(r.action, '', true),
      cell(targetOf(r), r.target_type ?? '', true),
      cell(subjectOf(r), '', true),
      r.error_code !== null ? { ...res, sub: String(r.error_code) } : res,
    ];
  },
  open: (c, r) => openOperationDrawer(c, r.id),
  rowActions: (): readonly ActionDef<OpRow>[] => [{ label: 'aDetail', run: (c, r) => openOperationDrawer(c, r.id) }],
  csvName: 'audit',
});

const PACK = { i18n: I18N_OPS, rewrites: REWRITES_OPS } as const;

export const PAGES_OPS: PageMap = {
  logs: { page: 'logs', ...PACK, tabLabels: (t) => strArr(t, 'tabLogs'), views: [webhookView, taskView, apiView] },
  alerts: { page: 'alerts', ...PACK, tabLabels: (t) => strArr(t, 'tabAlerts'), views: [alertsView('open'), alertsView('acked'), alertsView('all')] },
  audit: { page: 'audit', ...PACK, tabLabels: (t) => [str(t, 'all')], views: [auditView] },
};
